// Path: /home/ladmin/ESTATA/apps/0001-estata.com/frontend/src/layouts/AppLayout.tsx
// Version: v1.8.0
// Description: +CookieConsent banner (DSGVO-compliant)

import { useEffect, useRef, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import Footer from "../components/Footer.tsx";
import Header from "../components/Header.tsx";
import CookieConsent from "../components/CookieConsent.tsx";
import { initReveal } from "../hooks/useReveal.ts";
import { usePageStatus } from "../hooks/usePageStatus.tsx";
import NotFound from "../pages/NotFound.tsx";

const AppLayout = () => {
  const { isPublished, isLoading } = usePageStatus();
  const location = useLocation();
  const cleanupRef = useRef<(() => void) | null>(null);
  const mutationObserverRef = useRef<MutationObserver | null>(null);
  const mainRef = useRef<HTMLElement>(null);
  
  const [isPageReady, setIsPageReady] = useState(false);

  useEffect(() => {
    setIsPageReady(false);
    
    const checkIfReady = () => {
      const main = mainRef.current;
      if (!main) return false;
      
      const children = Array.from(main.children) as HTMLElement[];
      
      const realComponents = children.filter((el) => {
        const text = el.textContent || "";
        const isLoadingSpinner = 
          el.classList.contains("animate-pulse") ||
          el.classList.contains("animate-spin") ||
          text.includes("Loading") ||
          text.includes("Laden") ||
          (text.length < 20 && children.length === 1);
        return !isLoadingSpinner;
      });
      
      return realComponents.length >= 1;
    };

    const interval = setInterval(() => {
      if (checkIfReady()) {
        setIsPageReady(true);
        clearInterval(interval);
      }
    }, 50);

    const timeout = setTimeout(() => {
      setIsPageReady(true);
      clearInterval(interval);
    }, 1500);

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [location.pathname, isLoading]);

  useEffect(() => {
    if (!isPageReady) return;

    if (cleanupRef.current) {
      cleanupRef.current();
    }
    if (mutationObserverRef.current) {
      mutationObserverRef.current.disconnect();
    }

    const addRevealClasses = (container: Element) => {
      const children = Array.from(container.children) as HTMLElement[];
      children.forEach((el, idx) => {
        if (idx < 1) return;
        if (el.classList.contains("no-reveal")) return;
        if (el.tagName === "SCRIPT" || el.tagName === "STYLE") return;
        
        if (!el.classList.contains("reveal") && !el.classList.contains("visible")) {
          el.classList.add("reveal");
          if (!el.hasAttribute("data-reveal")) {
            el.setAttribute("data-reveal", "fade-up");
          }
        }
      });
    };

    const main = mainRef.current;
    if (main) {
      addRevealClasses(main);

      mutationObserverRef.current = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === "childList" && mutation.addedNodes.length > 0) {
            setTimeout(() => addRevealClasses(main), 50);
          }
        });
      });

      mutationObserverRef.current.observe(main, {
        childList: true,
        subtree: false,
      });
    }

    cleanupRef.current = initReveal({
      once: true,
      durationMs: 800,
      offset: 60,
    });

    return () => {
      if (cleanupRef.current) cleanupRef.current();
      if (mutationObserverRef.current) mutationObserverRef.current.disconnect();
    };
  }, [isPageReady, location.pathname]);

  if (!isPublished && !isLoading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-1">
          <NotFound />
        </main>
        <Footer />
        <CookieConsent />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-black">
      <Header />
      <div
        style={{
          opacity: isPageReady ? 1 : 0,
          transition: "opacity 0.3s ease-out",
          flex: 1,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <main ref={mainRef} className="flex-1">
          <Outlet />
        </main>
        <Footer />
      </div>
      <CookieConsent />
    </div>
  );
};

export default AppLayout;
